/* eslint-disable no-debugger */
import axios, { AxiosRequestConfig, AxiosResponse } from "axios";
import keycloak from "./keycloak";
import { toast } from "../components/ui/toast";
import { defaultCache, staticDataCache } from "../utils/cache";

const apiBaseUrl = import.meta.env.VITE_API_BASE_URL;

/**
 * Get the API base URL for non-Axios requests (SSE, WebSocket, etc.)
 */
export function getApiUrl(): string {
  return apiBaseUrl;
}

const api = axios.create({
  baseURL: apiBaseUrl,
  headers: {
    "Content-Type": "application/json",
  },
});

// Request deduplication: prevent duplicate concurrent requests
const pendingRequests = new Map<string, Promise<any>>();

/**
 * Generate cache key from request config
 */
function getCacheKey(config: AxiosRequestConfig): string {
  const { method, url, params, data } = config;
  const paramsStr = params ? JSON.stringify(params) : "";
  const dataStr = data ? JSON.stringify(data) : "";
  return `${method}:${url}:${paramsStr}:${dataStr}`;
}

/**
 * Check if request should be cached
 */
function shouldCache(config: AxiosRequestConfig): boolean {
  // Only cache GET requests
  if (config.method?.toLowerCase() !== "get") return false;

  // Don't cache requests that explicitly opt out
  if (config.headers?.["x-no-cache"]) return false;

  // Check if cache control header allows caching
  const cacheControl = config.headers?.["cache-control"];
  if (
    cacheControl?.includes("no-cache") ||
    cacheControl?.includes("no-store")
  ) {
    return false;
  }

  // Cache static/slow-changing endpoints
  const staticEndpoints = [
    "/organizations",
    "/workflows",
    "/channels",
    "/users",
  ];
  const url = config.url || "";
  return staticEndpoints.some(endpoint => url.includes(endpoint));
}

/**
 * Get cache instance for request
 */
function getCacheForRequest(config: AxiosRequestConfig) {
  // Use static data cache for static endpoints
  const staticEndpoints = ["/organizations", "/workflows"];
  const url = config.url || "";
  if (staticEndpoints.some(endpoint => url.includes(endpoint))) {
    return staticDataCache;
  }
  return defaultCache;
}

/**
 * Create a mock axios response from cached data
 */
function createCachedResponse(
  config: AxiosRequestConfig,
  data: any,
): AxiosResponse {
  return {
    data,
    status: 200,
    statusText: "OK",
    headers: {},
    config: config as any,
  } as AxiosResponse;
}

// Request interceptor
api.interceptors.request.use(
  async config => {
    const token = keycloak.token;
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Add organization ID header
    const currentOrg = localStorage.getItem("currentOrganization");
    if (currentOrg) {
      try {
        const org = JSON.parse(currentOrg);
        if (org.id) {
          config.headers["x-organization-id"] = org.id;
        }
      } catch (error) {
        console.warn(
          "Failed to parse currentOrganization from localStorage:",
          error,
        );
      }
    }

    // Request deduplication: check if same request is already pending
    const requestKey = getCacheKey(config);
    if (pendingRequests.has(requestKey)) {
      console.log(`Deduplicating request: ${config.url}`);
      return pendingRequests.get(requestKey)!;
    }

    // Check cache for GET requests (before making actual request)
    if (shouldCache(config)) {
      const cacheKey = getCacheKey(config);
      const cache = getCacheForRequest(config);
      const cached = cache.get(cacheKey);

      if (cached !== null) {
        console.log(`Cache hit for: ${config.url}`);
        // Mark config so response interceptor knows it's cached
        (config as any).__fromCache = true;
        (config as any).__cachedData = cached;
      }
    }

    // Log the final URL being constructed
    const finalUrl = `${apiBaseUrl}${config.url}`;
    console.log("Making API request to:", finalUrl);

    return config;
  },
  error => {
    return Promise.reject(error);
  },
);

// Response interceptor - success handler
api.interceptors.response.use(
  (response: AxiosResponse) => {
    const config = response.config;

    // Handle cached responses - return cached data instead of making request
    if ((config as any).__fromCache && (config as any).__cachedData) {
      const cachedResponse = createCachedResponse(
        config,
        (config as any).__cachedData,
      );
      const requestKey = getCacheKey(config);
      pendingRequests.delete(requestKey);
      return cachedResponse;
    }

    // Cache successful GET responses
    if (shouldCache(config) && response.status === 200) {
      const cacheKey = getCacheKey(config);
      const cache = getCacheForRequest(config);

      // Determine TTL based on endpoint
      let ttl: number | undefined;
      if (config.url?.includes("/analytics")) {
        ttl = 4 * 60 * 1000; // 4 minutes for analytics
      } else if (staticDataCache === getCacheForRequest(config)) {
        ttl = 30 * 60 * 1000; // 30 minutes for static data
      }

      cache.set(cacheKey, response.data, ttl);
      console.log(`Cached response for: ${config.url}`);
    }

    // Remove from pending requests
    const requestKey = getCacheKey(config);
    pendingRequests.delete(requestKey);

    return response;
  },
  error => {
    // Remove from pending requests on error
    if (error.config) {
      const requestKey = getCacheKey(error.config);
      pendingRequests.delete(requestKey);

      // Handle cached responses in error case
      if (
        (error.config as any).__fromCache &&
        (error.config as any).__cachedData
      ) {
        const cachedResponse = createCachedResponse(
          error.config,
          (error.config as any).__cachedData,
        );
        return Promise.resolve(cachedResponse);
      }
    }
    if (error.response?.status === 401) {
      toast.error("Session expired. Please login again.");
      keycloak.logout();
    } else if (error.response?.status === 402) {
      // Payment Required - handle subscription issues
      const errorData = error.response?.data;
      const errorCode = errorData?.code;
      const checkoutUrl = errorData?.checkoutUrl;
      const currentPath = window.location.pathname;

      // Map error codes to user-friendly messages
      const subscriptionErrorMessages: Record<string, string> = {
        NO_SUBSCRIPTION:
          "No subscription found. Please subscribe to a plan to continue.",
        PAYMENT_PAST_DUE:
          "Your subscription payment is past due. Please update your payment method.",
        SUBSCRIPTION_INACTIVE:
          errorData?.message ||
          "Your subscription is inactive. Please complete your subscription setup.",
        TRIAL_EXPIRED:
          "Your trial has expired. Please subscribe to continue using the service.",
      };

      const userMessage =
        subscriptionErrorMessages[errorCode] ||
        errorData?.message ||
        "Payment required. Please complete your subscription setup.";

      // Don't redirect if already on payment pages to prevent infinite loops
      if (
        currentPath.includes("/onboarding/payment-status") ||
        currentPath.startsWith("/onboarding") ||
        currentPath.startsWith("/accounts/")
      ) {
        // User is already on payment page, just show warning
        toast.warning(userMessage);
        return Promise.reject(error);
      }

      // Show user-friendly error message
      toast.error(userMessage, 5); // Show for 5 seconds

      // Log additional details for debugging
      console.warn("Subscription error:", {
        code: errorCode,
        status: errorData?.status,
        message: errorData?.message,
      });

      // Redirect to checkout if URL is provided
      if (checkoutUrl) {
        console.log("Redirecting to checkout:", checkoutUrl);
        setTimeout(() => {
          window.location.href = checkoutUrl;
        }, 1500); // Delay to allow user to read the message
      }

      return Promise.reject(error);
    } else if (error.response?.status === 403) {
      // Check for quota exceeded errors
      const errorCode = error.response?.data?.code;
      const errorData = error.response?.data;

      if (
        errorCode === "USAGE_LIMIT_EXCEEDED" ||
        errorCode === "MESSAGE_QUOTA_EXCEEDED" ||
        errorCode === "FEATURE_LIMIT_EXCEEDED"
      ) {
        // Don't show generic error - let the component handle it with pay-as-you-go dialog
        // The error object will contain quota information for components to use
        error.isQuotaError = true;
        error.quotaInfo = {
          feature: errorData.feature || "unknown",
          limit: errorData.limit,
          current: errorData.current,
          isOverage: errorData.isOverage,
          overageCost: errorData.overageCost,
        };
        // Mark as handled so catch blocks don't show duplicate toasts
        error.handledByInterceptor = true;
        if (errorCode === "FEATURE_LIMIT_EXCEEDED") {
          toast.error(errorData.message);
        } else if (errorData.isOverage) {
          toast.warning(
            `Quota exceeded. Pay-as-you-go is available. Estimated cost: $${(errorData.overageCost || 0).toFixed(4)}`,
          );
        } else {
          toast.warning(
            "Quota exceeded. Please enable pay-as-you-go in your plan settings or upgrade your plan.",
          );
        }
      } else {
        error.handledByInterceptor = true;
        const msg = errorData?.message || "Access denied. You don't have permission to perform this action.";
        toast.error(msg);
      }
    } else if (error.response?.status === 400) {
      const errData = error.response?.data;
      let errMsg: string | undefined;
      if (errData?.details?.message && Array.isArray(errData.details.message)) {
        errMsg = errData.details.message[0];
      }
      if (!errMsg && typeof errData?.message === "string") {
        const generic = ["Bad Request Exception", "Bad Request"];
        if (!generic.includes(errData.message)) {
          errMsg = errData.message;
        }
      }
      if (
        !errMsg &&
        Array.isArray(errData?.message) &&
        errData.message.length > 0
      ) {
        errMsg = errData.message[0];
      }
      if (errMsg) {
        toast.error(errMsg);
      }
    } else if (error.response?.status === 404) {
      // Don't show error message for 404 as it's expected when resources don't exist
      console.warn("Resource not found:", error.config?.url);
    } else if (error.response?.status >= 500) {
      toast.error("Server error. Please try again later.");
    } else {
      toast.error(error.response?.data?.message || "An error occurred");
    }
    return Promise.reject(error);
  },
);

export default api;
